import { useState, useEffect, useRef } from 'react'
import { bioThemes } from '../bioThemes'
import { apiRequest } from '@/lib/supabase'

export interface WizardLink {
  title: string
  url: string
}

export interface WizardState {
  pageType: 'links' | 'products' | 'both'
  display_name: string
  handle: string
  bio: string
  profile_image_url: string
  theme: string
  button_style: string
  button_color: string
  text_color: string
  font_family: string
  links: WizardLink[]
  is_published: boolean
}

const initialState: WizardState = {
  pageType: 'links',
  display_name: '',
  handle: '',
  bio: '',
  profile_image_url: '',
  theme: 'default',
  button_style: 'rounded',
  button_color: '',
  text_color: '',
  font_family: 'Inter',
  links: [],
  is_published: false,
}

export function useWizardState() {
  const [state, setState] = useState<WizardState>(initialState)
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null)
  const [checkingSlug, setCheckingSlug] = useState(false)
  const checkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function update(partial: Partial<WizardState>) {
    setState(prev => ({ ...prev, ...partial }))
  }

  function addLink(link: WizardLink) {
    setState(prev => ({
      ...prev,
      links: prev.links.length < 5 ? [...prev.links, link] : prev.links,
    }))
  }

  function removeLink(index: number) {
    setState(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }))
  }

  // Debounced slug availability check
  useEffect(() => {
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current)

    if (!state.handle || state.handle.length < 3) {
      setSlugAvailable(null)
      return
    }

    setCheckingSlug(true)
    checkTimerRef.current = setTimeout(async () => {
      try {
        const res = await apiRequest<{ success: boolean; data: Array<{ id: string; slug: string }> }>('/api/bio-pages')
        const taken = res.data.some(p => p.slug === state.handle)
        setSlugAvailable(!taken)
      } catch {
        setSlugAvailable(null)
      } finally {
        setCheckingSlug(false)
      }
    }, 400)

    return () => { if (checkTimerRef.current) clearTimeout(checkTimerRef.current) }
  }, [state.handle])

  function toApiPayload() {
    const theme = bioThemes[state.theme] || bioThemes.default
    return {
      title: state.display_name,
      slug: state.handle,
      bio: state.bio || null,
      profile_image_url: state.profile_image_url || null,
      display_name: state.display_name || null,
      handle: state.handle || null,
      theme: state.theme,
      button_style: state.button_style,
      button_color: state.button_color || theme.buttonBg,
      text_color: state.text_color || theme.textColor,
      font_family: state.font_family,
      is_published: state.is_published,
      background_type: 'color',
      background_value: null,
    }
  }

  function toPreviewPageData() {
    const theme = bioThemes[state.theme] || bioThemes.default
    return {
      title: state.display_name || 'Your Name',
      bio: state.bio || null,
      profile_image_url: state.profile_image_url || null,
      display_name: state.display_name || null,
      handle: state.handle || null,
      theme: state.theme,
      background_type: 'color' as const,
      background_value: null,
      button_style: state.button_style,
      button_color: state.button_color || theme.buttonBg,
      text_color: state.text_color || theme.textColor,
      font_family: state.font_family,
    }
  }

  function toPreviewLinks() {
    return state.links.map((link, i) => ({
      id: `wizard-${i}`,
      bio_page_id: '',
      title: link.title,
      url: link.url,
      icon: null,
      thumbnail_url: null,
      order_index: i,
      is_active: true,
      type: 'link',
      click_count: 0,
      scheduled_start_at: null,
      scheduled_end_at: null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_enabled: false,
      masked_slug: null,
    }))
  }

  return {
    state,
    update,
    addLink,
    removeLink,
    slugAvailable,
    checkingSlug,
    toApiPayload,
    toPreviewPageData,
    toPreviewLinks,
  }
}
